export interface ThemeDefinition {
  id: string;
  label: string;
  /** Preview swatch color for the theme picker (shown in both light and dark) */
  accentColor: string;
  cssVars: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

const CORE_CSS_VARS = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
] as const;

/**
 * Converts a tweakcn registry theme JSON into our ThemeDefinition format.
 * The tweakcn format uses bare variable names (e.g. "primary") while we use
 * CSS custom property names (e.g. "--primary"). Only color-related variables
 * are extracted; font/shadow/spacing vars are ignored since they would conflict
 * with our base stylesheet.
 */
export function parseTweakcnTheme(raw: {
  name: string;
  title?: string;
  cssVars: { light: Record<string, string>; dark: Record<string, string> };
}): ThemeDefinition {
  const coreSet = new Set<string>(CORE_CSS_VARS);
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};

  for (const key of Object.keys(raw.cssVars.light)) {
    if (coreSet.has(key)) {
      light[`--${key}`] = raw.cssVars.light[key];
    }
  }
  for (const key of Object.keys(raw.cssVars.dark)) {
    if (coreSet.has(key)) {
      dark[`--${key}`] = raw.cssVars.dark[key];
    }
  }

  const darkPrimary = dark['--primary'] ?? 'oklch(0.5 0 0)';
  const accentColor = oklchToHex(darkPrimary);

  return {
    id: raw.name,
    label: raw.title ?? kebabToTitle(raw.name),
    accentColor,
    cssVars: { light, dark },
  };
}

function kebabToTitle(s: string): string {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function oklchToHex(oklchStr: string): string {
  const m = oklchStr.match(/oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)/);
  if (!m) return '#888888';
  const L = parseFloat(m[1]);
  const C = parseFloat(m[2]);
  const H = parseFloat(m[3]) * (Math.PI / 180);
  const a_ = C * Math.cos(H);
  const b_ = C * Math.sin(H);
  const l_ = L + 0.3963377774 * a_ + 0.2158037573 * b_;
  const m_ = L - 0.1055613458 * a_ - 0.0638541728 * b_;
  const s_ = L - 0.0894841775 * a_ - 1.291485548 * b_;
  const l3 = l_ * l_ * l_;
  const m3 = m_ * m_ * m_;
  const s3 = s_ * s_ * s_;
  const r = +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  const g = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  const b = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3;
  const toSrgb = (x: number) =>
    Math.max(
      0,
      Math.min(
        255,
        Math.round((x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055) * 255),
      ),
    );
  return `#${[r, g, b]
    .map(toSrgb)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

// ---------------------------------------------------------------------------
// Neutral is the CSS default — matches index.css :root / .dark values exactly.
// It acts as the "no-op" theme (applying it just restores the stylesheet defaults).
// ---------------------------------------------------------------------------
const neutral: ThemeDefinition = {
  id: 'neutral',
  label: 'Neutral',
  accentColor: '#737373',
  cssVars: {
    light: {
      '--background': 'oklch(1 0 0)',
      '--foreground': 'oklch(0.145 0 0)',
      '--card': 'oklch(1 0 0)',
      '--card-foreground': 'oklch(0.145 0 0)',
      '--popover': 'oklch(1 0 0)',
      '--popover-foreground': 'oklch(0.145 0 0)',
      '--primary': 'oklch(0.205 0 0)',
      '--primary-foreground': 'oklch(0.985 0 0)',
      '--secondary': 'oklch(0.97 0 0)',
      '--secondary-foreground': 'oklch(0.205 0 0)',
      '--muted': 'oklch(0.97 0 0)',
      '--muted-foreground': 'oklch(0.556 0 0)',
      '--accent': 'oklch(0.97 0 0)',
      '--accent-foreground': 'oklch(0.205 0 0)',
      '--destructive': 'oklch(0.577 0.245 27.325)',
      '--border': 'oklch(0.922 0 0)',
      '--input': 'oklch(0.922 0 0)',
      '--ring': 'oklch(0.708 0 0)',
      '--chart-1': 'oklch(0.646 0.222 41.116)',
      '--chart-2': 'oklch(0.6 0.118 184.704)',
      '--chart-3': 'oklch(0.398 0.07 227.392)',
      '--chart-4': 'oklch(0.828 0.189 84.429)',
      '--chart-5': 'oklch(0.769 0.188 70.08)',
      '--sidebar': 'oklch(0.985 0 0)',
      '--sidebar-foreground': 'oklch(0.145 0 0)',
      '--sidebar-primary': 'oklch(0.205 0 0)',
      '--sidebar-primary-foreground': 'oklch(0.985 0 0)',
      '--sidebar-accent': 'oklch(0.97 0 0)',
      '--sidebar-accent-foreground': 'oklch(0.205 0 0)',
      '--sidebar-border': 'oklch(0.922 0 0)',
      '--sidebar-ring': 'oklch(0.708 0 0)',
    },
    dark: {
      '--background': 'oklch(0.13 0.004 285)',
      '--foreground': 'oklch(0.93 0 0)',
      '--card': 'oklch(0.17 0.005 285)',
      '--card-foreground': 'oklch(0.93 0 0)',
      '--popover': 'oklch(0.19 0.006 285)',
      '--popover-foreground': 'oklch(0.93 0 0)',
      '--primary': 'oklch(0.93 0 0)',
      '--primary-foreground': 'oklch(0.17 0.005 285)',
      '--secondary': 'oklch(0.22 0.006 285)',
      '--secondary-foreground': 'oklch(0.93 0 0)',
      '--muted': 'oklch(0.22 0.006 285)',
      '--muted-foreground': 'oklch(0.63 0 0)',
      '--accent': 'oklch(0.22 0.006 285)',
      '--accent-foreground': 'oklch(0.93 0 0)',
      '--destructive': 'oklch(0.704 0.191 22.216)',
      '--border': 'oklch(1 0 0 / 8%)',
      '--input': 'oklch(1 0 0 / 12%)',
      '--ring': 'oklch(0.65 0.18 285)',
      '--chart-1': 'oklch(0.65 0.18 285)',
      '--chart-2': 'oklch(0.696 0.17 162.48)',
      '--chart-3': 'oklch(0.769 0.188 70.08)',
      '--chart-4': 'oklch(0.627 0.265 303.9)',
      '--chart-5': 'oklch(0.645 0.246 16.439)',
      '--sidebar': 'oklch(0.15 0.005 285)',
      '--sidebar-foreground': 'oklch(0.93 0 0)',
      '--sidebar-primary': 'oklch(0.65 0.18 285)',
      '--sidebar-primary-foreground': 'oklch(0.98 0 0)',
      '--sidebar-accent': 'oklch(0.22 0.008 285)',
      '--sidebar-accent-foreground': 'oklch(0.93 0 0)',
      '--sidebar-border': 'oklch(1 0 0 / 8%)',
      '--sidebar-ring': 'oklch(0.65 0.18 285)',
    },
  },
};

// ---------------------------------------------------------------------------
// Curated themes from tweakcn.com — converted at development time using
// parseTweakcnTheme(). These are full-palette themes that dramatically change
// the look and feel beyond just accent colors.
// ---------------------------------------------------------------------------

const t3Chat: ThemeDefinition = parseTweakcnTheme({
  name: 't3-chat',
  title: 'T3 Chat',
  cssVars: {
    light: {
      background: 'oklch(0.9754 0.0084 325.6414)',
      foreground: 'oklch(0.3257 0.1161 325.0372)',
      card: 'oklch(0.9754 0.0084 325.6414)',
      'card-foreground': 'oklch(0.3257 0.1161 325.0372)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.3257 0.1161 325.0372)',
      primary: 'oklch(0.5316 0.1409 355.1999)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.8696 0.0675 334.8991)',
      'secondary-foreground': 'oklch(0.4448 0.1341 324.7991)',
      muted: 'oklch(0.9395 0.0260 331.5454)',
      'muted-foreground': 'oklch(0.4924 0.1244 324.4523)',
      accent: 'oklch(0.8696 0.0675 334.8991)',
      'accent-foreground': 'oklch(0.4448 0.1341 324.7991)',
      destructive: 'oklch(0.5248 0.1368 20.8317)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8568 0.0829 328.9110)',
      input: 'oklch(0.8517 0.0558 336.6002)',
      ring: 'oklch(0.5916 0.2180 0.5844)',
      'chart-1': 'oklch(0.6038 0.2363 344.4657)',
      'chart-2': 'oklch(0.4445 0.2251 300.6246)',
      'chart-3': 'oklch(0.3790 0.0438 226.1538)',
      'chart-4': 'oklch(0.8330 0.1185 88.3461)',
      'chart-5': 'oklch(0.7843 0.1256 58.9964)',
      sidebar: 'oklch(0.9360 0.0288 320.5788)',
      'sidebar-foreground': 'oklch(0.4948 0.1909 354.5435)',
      'sidebar-primary': 'oklch(0.3963 0.0251 285.1962)',
      'sidebar-primary-foreground': 'oklch(0.9668 0.0124 337.5228)',
      'sidebar-accent': 'oklch(0.9789 0.0013 106.4235)',
      'sidebar-accent-foreground': 'oklch(0.3963 0.0251 285.1962)',
      'sidebar-border': 'oklch(0.9383 0.0026 48.7178)',
      'sidebar-ring': 'oklch(0.5916 0.2180 0.5844)',
    },
    dark: {
      background: 'oklch(0.2409 0.0201 307.5346)',
      foreground: 'oklch(0.8398 0.0387 309.5391)',
      card: 'oklch(0.2803 0.0232 307.5413)',
      'card-foreground': 'oklch(0.8456 0.0302 341.4597)',
      popover: 'oklch(0.1548 0.0132 338.9015)',
      'popover-foreground': 'oklch(0.9647 0.0091 341.8035)',
      primary: 'oklch(0.4607 0.1853 4.0994)',
      'primary-foreground': 'oklch(0.8560 0.0618 346.3684)',
      secondary: 'oklch(0.3137 0.0306 310.0610)',
      'secondary-foreground': 'oklch(0.8483 0.0382 307.9613)',
      muted: 'oklch(0.2634 0.0219 309.4748)',
      'muted-foreground': 'oklch(0.7940 0.0372 307.1032)',
      accent: 'oklch(0.3649 0.0508 308.4911)',
      'accent-foreground': 'oklch(0.9647 0.0091 341.8035)',
      destructive: 'oklch(0.7040 0.1910 12.6119)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3286 0.0154 343.4461)',
      input: 'oklch(0.3387 0.0195 332.8347)',
      ring: 'oklch(0.5916 0.2180 0.5844)',
      'chart-1': 'oklch(0.5316 0.1409 355.1999)',
      'chart-2': 'oklch(0.5633 0.1912 306.8561)',
      'chart-3': 'oklch(0.7227 0.1502 60.5799)',
      'chart-4': 'oklch(0.6193 0.2029 312.7422)',
      'chart-5': 'oklch(0.6118 0.2093 6.1387)',
      sidebar: 'oklch(0.1893 0.0163 331.0475)',
      'sidebar-foreground': 'oklch(0.8607 0.0293 343.6612)',
      'sidebar-primary': 'oklch(0.4882 0.2172 264.3763)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.2337 0.0261 338.1961)',
      'sidebar-accent-foreground': 'oklch(0.9674 0.0013 286.3752)',
      'sidebar-border': 'oklch(0 0 0)',
      'sidebar-ring': 'oklch(0.5916 0.2180 0.5844)',
    },
  },
});

const catppuccin: ThemeDefinition = parseTweakcnTheme({
  name: 'catppuccin',
  title: 'Catppuccin',
  cssVars: {
    light: {
      background: 'oklch(0.9578 0.0058 264.5321)',
      foreground: 'oklch(0.4355 0.0430 279.3250)',
      card: 'oklch(1.0000 0 0)',
      'card-foreground': 'oklch(0.4355 0.0430 279.3250)',
      popover: 'oklch(0.8575 0.0145 268.4756)',
      'popover-foreground': 'oklch(0.4355 0.0430 279.3250)',
      primary: 'oklch(0.5547 0.2503 297.0156)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.8575 0.0145 268.4756)',
      'secondary-foreground': 'oklch(0.4355 0.0430 279.3250)',
      muted: 'oklch(0.9060 0.0117 264.5071)',
      'muted-foreground': 'oklch(0.5471 0.0343 279.0837)',
      accent: 'oklch(0.6820 0.1448 235.3822)',
      'accent-foreground': 'oklch(1.0000 0 0)',
      destructive: 'oklch(0.5505 0.2155 19.8095)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8083 0.0174 271.1982)',
      input: 'oklch(0.8575 0.0145 268.4756)',
      ring: 'oklch(0.5547 0.2503 297.0156)',
      'chart-1': 'oklch(0.5547 0.2503 297.0156)',
      'chart-2': 'oklch(0.6820 0.1448 235.3822)',
      'chart-3': 'oklch(0.6250 0.1772 140.4448)',
      'chart-4': 'oklch(0.6920 0.2041 42.4293)',
      'chart-5': 'oklch(0.7141 0.1045 33.0967)',
      sidebar: 'oklch(0.9335 0.0087 264.5206)',
      'sidebar-foreground': 'oklch(0.4355 0.0430 279.3250)',
      'sidebar-primary': 'oklch(0.5547 0.2503 297.0156)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.6820 0.1448 235.3822)',
      'sidebar-accent-foreground': 'oklch(1.0000 0 0)',
      'sidebar-border': 'oklch(0.8083 0.0174 271.1982)',
      'sidebar-ring': 'oklch(0.5547 0.2503 297.0156)',
    },
    dark: {
      background: 'oklch(0.2155 0.0254 284.0647)',
      foreground: 'oklch(0.8787 0.0426 272.2767)',
      card: 'oklch(0.2429 0.0304 283.9110)',
      'card-foreground': 'oklch(0.8787 0.0426 272.2767)',
      popover: 'oklch(0.4037 0.0320 280.1520)',
      'popover-foreground': 'oklch(0.8787 0.0426 272.2767)',
      primary: 'oklch(0.7871 0.1187 304.7693)',
      'primary-foreground': 'oklch(0.2429 0.0304 283.9110)',
      secondary: 'oklch(0.4765 0.0340 278.6430)',
      'secondary-foreground': 'oklch(0.8787 0.0426 272.2767)',
      muted: 'oklch(0.2973 0.0294 276.2144)',
      'muted-foreground': 'oklch(0.7510 0.0396 273.9320)',
      accent: 'oklch(0.8467 0.0833 210.2545)',
      'accent-foreground': 'oklch(0.2429 0.0304 283.9110)',
      destructive: 'oklch(0.7556 0.1297 2.7642)',
      'destructive-foreground': 'oklch(0.2429 0.0304 283.9110)',
      border: 'oklch(0.3240 0.0319 281.9784)',
      input: 'oklch(0.3240 0.0319 281.9784)',
      ring: 'oklch(0.7871 0.1187 304.7693)',
      'chart-1': 'oklch(0.7871 0.1187 304.7693)',
      'chart-2': 'oklch(0.8467 0.0833 210.2545)',
      'chart-3': 'oklch(0.8577 0.1092 142.7153)',
      'chart-4': 'oklch(0.8237 0.1015 52.6294)',
      'chart-5': 'oklch(0.9226 0.0238 30.4919)',
      sidebar: 'oklch(0.1828 0.0204 284.2039)',
      'sidebar-foreground': 'oklch(0.8787 0.0426 272.2767)',
      'sidebar-primary': 'oklch(0.7871 0.1187 304.7693)',
      'sidebar-primary-foreground': 'oklch(0.2429 0.0304 283.9110)',
      'sidebar-accent': 'oklch(0.8467 0.0833 210.2545)',
      'sidebar-accent-foreground': 'oklch(0.2429 0.0304 283.9110)',
      'sidebar-border': 'oklch(0.4037 0.0320 280.1520)',
      'sidebar-ring': 'oklch(0.7871 0.1187 304.7693)',
    },
  },
});

const claude: ThemeDefinition = parseTweakcnTheme({
  name: 'claude',
  title: 'Claude',
  cssVars: {
    light: {
      background: 'oklch(0.9818 0.0054 95.0986)',
      foreground: 'oklch(0.3438 0.0269 95.7226)',
      card: 'oklch(0.9818 0.0054 95.0986)',
      'card-foreground': 'oklch(0.1908 0.0020 106.5859)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.2671 0.0196 98.9390)',
      primary: 'oklch(0.6171 0.1375 39.0427)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.9245 0.0138 92.9892)',
      'secondary-foreground': 'oklch(0.4334 0.0177 98.6048)',
      muted: 'oklch(0.9341 0.0153 90.2390)',
      'muted-foreground': 'oklch(0.6059 0.0075 97.4233)',
      accent: 'oklch(0.9245 0.0138 92.9892)',
      'accent-foreground': 'oklch(0.2671 0.0196 98.9390)',
      destructive: 'oklch(0.1908 0.0020 106.5859)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8847 0.0069 97.3627)',
      input: 'oklch(0.7621 0.0156 98.3528)',
      ring: 'oklch(0.6171 0.1375 39.0427)',
      'chart-1': 'oklch(0.5583 0.1276 42.9956)',
      'chart-2': 'oklch(0.6898 0.1581 290.4107)',
      'chart-3': 'oklch(0.8816 0.0276 93.1280)',
      'chart-4': 'oklch(0.8822 0.0403 298.1792)',
      'chart-5': 'oklch(0.5608 0.1348 42.0584)',
      sidebar: 'oklch(0.9663 0.0080 98.8792)',
      'sidebar-foreground': 'oklch(0.3590 0.0051 106.6524)',
      'sidebar-primary': 'oklch(0.6171 0.1375 39.0427)',
      'sidebar-primary-foreground': 'oklch(0.9881 0 0)',
      'sidebar-accent': 'oklch(0.9245 0.0138 92.9892)',
      'sidebar-accent-foreground': 'oklch(0.3250 0 0)',
      'sidebar-border': 'oklch(0.9401 0 0)',
      'sidebar-ring': 'oklch(0.7731 0 0)',
    },
    dark: {
      background: 'oklch(0.2679 0.0036 106.6427)',
      foreground: 'oklch(0.8074 0.0142 93.0137)',
      card: 'oklch(0.2679 0.0036 106.6427)',
      'card-foreground': 'oklch(0.9818 0.0054 95.0986)',
      popover: 'oklch(0.3085 0.0035 106.6039)',
      'popover-foreground': 'oklch(0.9211 0.0040 106.4781)',
      primary: 'oklch(0.6724 0.1308 38.7559)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.9818 0.0054 95.0986)',
      'secondary-foreground': 'oklch(0.3085 0.0035 106.6039)',
      muted: 'oklch(0.2213 0.0038 106.7070)',
      'muted-foreground': 'oklch(0.7713 0.0169 99.0657)',
      accent: 'oklch(0.2130 0.0078 95.4245)',
      'accent-foreground': 'oklch(0.9663 0.0080 98.8792)',
      destructive: 'oklch(0.6368 0.2078 25.3313)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3618 0.0101 106.8928)',
      input: 'oklch(0.4336 0.0113 100.2195)',
      ring: 'oklch(0.6724 0.1308 38.7559)',
      'chart-1': 'oklch(0.5583 0.1276 42.9956)',
      'chart-2': 'oklch(0.6898 0.1581 290.4107)',
      'chart-3': 'oklch(0.2130 0.0078 95.4245)',
      'chart-4': 'oklch(0.3074 0.0516 289.3230)',
      'chart-5': 'oklch(0.5608 0.1348 42.0584)',
      sidebar: 'oklch(0.2357 0.0024 67.7077)',
      'sidebar-foreground': 'oklch(0.8074 0.0142 93.0137)',
      'sidebar-primary': 'oklch(0.3250 0 0)',
      'sidebar-primary-foreground': 'oklch(0.9881 0 0)',
      'sidebar-accent': 'oklch(0.1680 0.0020 106.6177)',
      'sidebar-accent-foreground': 'oklch(0.8074 0.0142 93.0137)',
      'sidebar-border': 'oklch(0.9401 0 0)',
      'sidebar-ring': 'oklch(0.7731 0 0)',
    },
  },
});

const vercel: ThemeDefinition = parseTweakcnTheme({
  name: 'vercel',
  title: 'Vercel',
  cssVars: {
    light: {
      background: 'oklch(0.9900 0 0)',
      foreground: 'oklch(0 0 0)',
      card: 'oklch(1 0 0)',
      'card-foreground': 'oklch(0 0 0)',
      popover: 'oklch(0.9900 0 0)',
      'popover-foreground': 'oklch(0 0 0)',
      primary: 'oklch(0 0 0)',
      'primary-foreground': 'oklch(1 0 0)',
      secondary: 'oklch(0.9400 0 0)',
      'secondary-foreground': 'oklch(0 0 0)',
      muted: 'oklch(0.9700 0 0)',
      'muted-foreground': 'oklch(0.4400 0 0)',
      accent: 'oklch(0.9400 0 0)',
      'accent-foreground': 'oklch(0 0 0)',
      destructive: 'oklch(0.6300 0.1900 23.0300)',
      'destructive-foreground': 'oklch(1 0 0)',
      border: 'oklch(0.9200 0 0)',
      input: 'oklch(0.9400 0 0)',
      ring: 'oklch(0 0 0)',
      'chart-1': 'oklch(0.8100 0.1700 75.3500)',
      'chart-2': 'oklch(0.5500 0.2200 264.5300)',
      'chart-3': 'oklch(0.7200 0 0)',
      'chart-4': 'oklch(0.9200 0 0)',
      'chart-5': 'oklch(0.5600 0 0)',
      sidebar: 'oklch(0.9900 0 0)',
      'sidebar-foreground': 'oklch(0 0 0)',
      'sidebar-primary': 'oklch(0 0 0)',
      'sidebar-primary-foreground': 'oklch(1 0 0)',
      'sidebar-accent': 'oklch(0.9400 0 0)',
      'sidebar-accent-foreground': 'oklch(0 0 0)',
      'sidebar-border': 'oklch(0.9400 0 0)',
      'sidebar-ring': 'oklch(0 0 0)',
    },
    dark: {
      background: 'oklch(0 0 0)',
      foreground: 'oklch(1 0 0)',
      card: 'oklch(0.1400 0 0)',
      'card-foreground': 'oklch(1 0 0)',
      popover: 'oklch(0.1800 0 0)',
      'popover-foreground': 'oklch(1 0 0)',
      primary: 'oklch(1 0 0)',
      'primary-foreground': 'oklch(0 0 0)',
      secondary: 'oklch(0.2500 0 0)',
      'secondary-foreground': 'oklch(1 0 0)',
      muted: 'oklch(0.2300 0 0)',
      'muted-foreground': 'oklch(0.7200 0 0)',
      accent: 'oklch(0.3200 0 0)',
      'accent-foreground': 'oklch(1 0 0)',
      destructive: 'oklch(0.6900 0.2000 23.9100)',
      'destructive-foreground': 'oklch(0 0 0)',
      border: 'oklch(0.2600 0 0)',
      input: 'oklch(0.3200 0 0)',
      ring: 'oklch(0.7200 0 0)',
      'chart-1': 'oklch(0.8100 0.1700 75.3500)',
      'chart-2': 'oklch(0.5800 0.2100 260.8400)',
      'chart-3': 'oklch(0.5600 0 0)',
      'chart-4': 'oklch(0.4400 0 0)',
      'chart-5': 'oklch(0.9200 0 0)',
      sidebar: 'oklch(0.1800 0 0)',
      'sidebar-foreground': 'oklch(1 0 0)',
      'sidebar-primary': 'oklch(1 0 0)',
      'sidebar-primary-foreground': 'oklch(0 0 0)',
      'sidebar-accent': 'oklch(0.3200 0 0)',
      'sidebar-accent-foreground': 'oklch(1 0 0)',
      'sidebar-border': 'oklch(0.3200 0 0)',
      'sidebar-ring': 'oklch(0.7200 0 0)',
    },
  },
});

const mono: ThemeDefinition = parseTweakcnTheme({
  name: 'mono',
  title: 'Mono',
  cssVars: {
    light: {
      background: 'oklch(1.0000 0 0)',
      foreground: 'oklch(0.1448 0 0)',
      card: 'oklch(1.0000 0 0)',
      'card-foreground': 'oklch(0.1448 0 0)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.1448 0 0)',
      primary: 'oklch(0.5555 0 0)',
      'primary-foreground': 'oklch(0.9851 0 0)',
      secondary: 'oklch(0.9702 0 0)',
      'secondary-foreground': 'oklch(0.2046 0 0)',
      muted: 'oklch(0.9702 0 0)',
      'muted-foreground': 'oklch(0.5486 0 0)',
      accent: 'oklch(0.9702 0 0)',
      'accent-foreground': 'oklch(0.2046 0 0)',
      destructive: 'oklch(0.5830 0.2387 28.4765)',
      'destructive-foreground': 'oklch(0.9702 0 0)',
      border: 'oklch(0.9219 0 0)',
      input: 'oklch(0.9219 0 0)',
      ring: 'oklch(0.7090 0 0)',
      'chart-1': 'oklch(0.5555 0 0)',
      'chart-2': 'oklch(0.5555 0 0)',
      'chart-3': 'oklch(0.5555 0 0)',
      'chart-4': 'oklch(0.5555 0 0)',
      'chart-5': 'oklch(0.5555 0 0)',
      sidebar: 'oklch(0.9851 0 0)',
      'sidebar-foreground': 'oklch(0.1448 0 0)',
      'sidebar-primary': 'oklch(0.2046 0 0)',
      'sidebar-primary-foreground': 'oklch(0.9851 0 0)',
      'sidebar-accent': 'oklch(0.9702 0 0)',
      'sidebar-accent-foreground': 'oklch(0.2046 0 0)',
      'sidebar-border': 'oklch(0.9219 0 0)',
      'sidebar-ring': 'oklch(0.7090 0 0)',
    },
    dark: {
      background: 'oklch(0.1448 0 0)',
      foreground: 'oklch(0.9851 0 0)',
      card: 'oklch(0.2134 0 0)',
      'card-foreground': 'oklch(0.9851 0 0)',
      popover: 'oklch(0.2686 0 0)',
      'popover-foreground': 'oklch(0.9851 0 0)',
      primary: 'oklch(0.5555 0 0)',
      'primary-foreground': 'oklch(0.9851 0 0)',
      secondary: 'oklch(0.2686 0 0)',
      'secondary-foreground': 'oklch(0.9851 0 0)',
      muted: 'oklch(0.2686 0 0)',
      'muted-foreground': 'oklch(0.7090 0 0)',
      accent: 'oklch(0.3715 0 0)',
      'accent-foreground': 'oklch(0.9851 0 0)',
      destructive: 'oklch(0.7022 0.1892 22.2279)',
      'destructive-foreground': 'oklch(0.2686 0 0)',
      border: 'oklch(0.3407 0 0)',
      input: 'oklch(0.4386 0 0)',
      ring: 'oklch(0.5555 0 0)',
      'chart-1': 'oklch(0.5555 0 0)',
      'chart-2': 'oklch(0.5555 0 0)',
      'chart-3': 'oklch(0.5555 0 0)',
      'chart-4': 'oklch(0.5555 0 0)',
      'chart-5': 'oklch(0.5555 0 0)',
      sidebar: 'oklch(0.2046 0 0)',
      'sidebar-foreground': 'oklch(0.9851 0 0)',
      'sidebar-primary': 'oklch(0.9851 0 0)',
      'sidebar-primary-foreground': 'oklch(0.2046 0 0)',
      'sidebar-accent': 'oklch(0.2686 0 0)',
      'sidebar-accent-foreground': 'oklch(0.9851 0 0)',
      'sidebar-border': 'oklch(1.0000 0 0)',
      'sidebar-ring': 'oklch(0.4386 0 0)',
    },
  },
});

const cyberpunk: ThemeDefinition = parseTweakcnTheme({
  name: 'cyberpunk',
  title: 'Cyberpunk',
  cssVars: {
    light: {
      background: 'oklch(0.9816 0.0017 247.8390)',
      foreground: 'oklch(0.1649 0.0352 281.8285)',
      card: 'oklch(1.0000 0 0)',
      'card-foreground': 'oklch(0.1649 0.0352 281.8285)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.1649 0.0352 281.8285)',
      primary: 'oklch(0.6726 0.2904 341.4084)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.9595 0.0200 286.0164)',
      'secondary-foreground': 'oklch(0.1649 0.0352 281.8285)',
      muted: 'oklch(0.9595 0.0200 286.0164)',
      'muted-foreground': 'oklch(0.1649 0.0352 281.8285)',
      accent: 'oklch(0.8903 0.1739 171.2690)',
      'accent-foreground': 'oklch(0.1649 0.0352 281.8285)',
      destructive: 'oklch(0.6535 0.2348 34.0370)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.9205 0.0086 225.0878)',
      input: 'oklch(0.9205 0.0086 225.0878)',
      ring: 'oklch(0.6726 0.2904 341.4084)',
      'chart-1': 'oklch(0.6726 0.2904 341.4084)',
      'chart-2': 'oklch(0.5488 0.2944 299.0954)',
      'chart-3': 'oklch(0.8442 0.1457 209.2851)',
      'chart-4': 'oklch(0.8903 0.1739 171.2690)',
      'chart-5': 'oklch(0.9168 0.1915 101.4070)',
      sidebar: 'oklch(0.9595 0.0200 286.0164)',
      'sidebar-foreground': 'oklch(0.1649 0.0352 281.8285)',
      'sidebar-primary': 'oklch(0.6726 0.2904 341.4084)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.8903 0.1739 171.2690)',
      'sidebar-accent-foreground': 'oklch(0.1649 0.0352 281.8285)',
      'sidebar-border': 'oklch(0.9205 0.0086 225.0878)',
      'sidebar-ring': 'oklch(0.6726 0.2904 341.4084)',
    },
    dark: {
      background: 'oklch(0.1649 0.0352 281.8285)',
      foreground: 'oklch(0.9513 0.0074 260.7315)',
      card: 'oklch(0.2542 0.0611 281.1423)',
      'card-foreground': 'oklch(0.9513 0.0074 260.7315)',
      popover: 'oklch(0.2542 0.0611 281.1423)',
      'popover-foreground': 'oklch(0.9513 0.0074 260.7315)',
      primary: 'oklch(0.6726 0.2904 341.4084)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.2542 0.0611 281.1423)',
      'secondary-foreground': 'oklch(0.9513 0.0074 260.7315)',
      muted: 'oklch(0.2123 0.0522 280.9917)',
      'muted-foreground': 'oklch(0.6245 0.0500 278.1046)',
      accent: 'oklch(0.8903 0.1739 171.2690)',
      'accent-foreground': 'oklch(0.1649 0.0352 281.8285)',
      destructive: 'oklch(0.6535 0.2348 34.0370)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3279 0.0832 280.7890)',
      input: 'oklch(0.3279 0.0832 280.7890)',
      ring: 'oklch(0.6726 0.2904 341.4084)',
      'chart-1': 'oklch(0.6726 0.2904 341.4084)',
      'chart-2': 'oklch(0.5488 0.2944 299.0954)',
      'chart-3': 'oklch(0.8442 0.1457 209.2851)',
      'chart-4': 'oklch(0.8903 0.1739 171.2690)',
      'chart-5': 'oklch(0.9168 0.1915 101.4070)',
      sidebar: 'oklch(0.1649 0.0352 281.8285)',
      'sidebar-foreground': 'oklch(0.9513 0.0074 260.7315)',
      'sidebar-primary': 'oklch(0.6726 0.2904 341.4084)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.8903 0.1739 171.2690)',
      'sidebar-accent-foreground': 'oklch(0.1649 0.0352 281.8285)',
      'sidebar-border': 'oklch(0.3279 0.0832 280.7890)',
      'sidebar-ring': 'oklch(0.6726 0.2904 341.4084)',
    },
  },
});

const doom64: ThemeDefinition = parseTweakcnTheme({
  name: 'doom-64',
  title: 'Doom 64',
  cssVars: {
    light: {
      background: 'oklch(0.8452 0 0)',
      foreground: 'oklch(0.2393 0 0)',
      card: 'oklch(0.7572 0 0)',
      'card-foreground': 'oklch(0.2393 0 0)',
      popover: 'oklch(0.7572 0 0)',
      'popover-foreground': 'oklch(0.2393 0 0)',
      primary: 'oklch(0.5016 0.1887 27.4816)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.4955 0.0896 126.1858)',
      'secondary-foreground': 'oklch(1.0000 0 0)',
      muted: 'oklch(0.7826 0 0)',
      'muted-foreground': 'oklch(0.4091 0 0)',
      accent: 'oklch(0.5880 0.0993 245.7394)',
      'accent-foreground': 'oklch(1.0000 0 0)',
      destructive: 'oklch(0.7076 0.1975 46.4558)',
      'destructive-foreground': 'oklch(0 0 0)',
      border: 'oklch(0.4313 0 0)',
      input: 'oklch(0.4313 0 0)',
      ring: 'oklch(0.5016 0.1887 27.4816)',
      'chart-1': 'oklch(0.5016 0.1887 27.4816)',
      'chart-2': 'oklch(0.4955 0.0896 126.1858)',
      'chart-3': 'oklch(0.5880 0.0993 245.7394)',
      'chart-4': 'oklch(0.7076 0.1975 46.4558)',
      'chart-5': 'oklch(0.5656 0.0431 40.4319)',
      sidebar: 'oklch(0.7572 0 0)',
      'sidebar-foreground': 'oklch(0.2393 0 0)',
      'sidebar-primary': 'oklch(0.5016 0.1887 27.4816)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.5880 0.0993 245.7394)',
      'sidebar-accent-foreground': 'oklch(1.0000 0 0)',
      'sidebar-border': 'oklch(0.4313 0 0)',
      'sidebar-ring': 'oklch(0.5016 0.1887 27.4816)',
    },
    dark: {
      background: 'oklch(0.2178 0 0)',
      foreground: 'oklch(0.9067 0 0)',
      card: 'oklch(0.2850 0 0)',
      'card-foreground': 'oklch(0.9067 0 0)',
      popover: 'oklch(0.2850 0 0)',
      'popover-foreground': 'oklch(0.9067 0 0)',
      primary: 'oklch(0.6083 0.2090 27.0276)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.6423 0.1467 133.0145)',
      'secondary-foreground': 'oklch(0 0 0)',
      muted: 'oklch(0.2645 0 0)',
      'muted-foreground': 'oklch(0.7058 0 0)',
      accent: 'oklch(0.7482 0.1235 244.7492)',
      'accent-foreground': 'oklch(0 0 0)',
      destructive: 'oklch(0.7839 0.1719 68.0943)',
      'destructive-foreground': 'oklch(0 0 0)',
      border: 'oklch(0.4091 0 0)',
      input: 'oklch(0.4091 0 0)',
      ring: 'oklch(0.6083 0.2090 27.0276)',
      'chart-1': 'oklch(0.6083 0.2090 27.0276)',
      'chart-2': 'oklch(0.6423 0.1467 133.0145)',
      'chart-3': 'oklch(0.7482 0.1235 244.7492)',
      'chart-4': 'oklch(0.7839 0.1719 68.0943)',
      'chart-5': 'oklch(0.6471 0.0334 40.7963)',
      sidebar: 'oklch(0.1913 0 0)',
      'sidebar-foreground': 'oklch(0.9067 0 0)',
      'sidebar-primary': 'oklch(0.6083 0.2090 27.0276)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.7482 0.1235 244.7492)',
      'sidebar-accent-foreground': 'oklch(0 0 0)',
      'sidebar-border': 'oklch(0.4091 0 0)',
      'sidebar-ring': 'oklch(0.6083 0.2090 27.0276)',
    },
  },
});

const supabase: ThemeDefinition = parseTweakcnTheme({
  name: 'supabase',
  title: 'Supabase',
  cssVars: {
    light: {
      background: 'oklch(0.9911 0 0)',
      foreground: 'oklch(0.2046 0 0)',
      card: 'oklch(0.9911 0 0)',
      'card-foreground': 'oklch(0.2046 0 0)',
      popover: 'oklch(0.9911 0 0)',
      'popover-foreground': 'oklch(0.4386 0 0)',
      primary: 'oklch(0.8348 0.1302 160.9080)',
      'primary-foreground': 'oklch(0.2626 0.0147 166.4589)',
      secondary: 'oklch(0.9940 0 0)',
      'secondary-foreground': 'oklch(0.2046 0 0)',
      muted: 'oklch(0.9461 0 0)',
      'muted-foreground': 'oklch(0.2435 0 0)',
      accent: 'oklch(0.9461 0 0)',
      'accent-foreground': 'oklch(0.2435 0 0)',
      destructive: 'oklch(0.5523 0.1927 32.7272)',
      'destructive-foreground': 'oklch(0.9934 0.0032 17.2118)',
      border: 'oklch(0.9037 0 0)',
      input: 'oklch(0.9731 0 0)',
      ring: 'oklch(0.8348 0.1302 160.9080)',
      'chart-1': 'oklch(0.8348 0.1302 160.9080)',
      'chart-2': 'oklch(0.6231 0.1880 259.8145)',
      'chart-3': 'oklch(0.6056 0.2189 292.7172)',
      'chart-4': 'oklch(0.7686 0.1647 70.0804)',
      'chart-5': 'oklch(0.6959 0.1491 162.4796)',
      sidebar: 'oklch(0.9911 0 0)',
      'sidebar-foreground': 'oklch(0.5452 0 0)',
      'sidebar-primary': 'oklch(0.8348 0.1302 160.9080)',
      'sidebar-primary-foreground': 'oklch(0.2626 0.0147 166.4589)',
      'sidebar-accent': 'oklch(0.9461 0 0)',
      'sidebar-accent-foreground': 'oklch(0.2435 0 0)',
      'sidebar-border': 'oklch(0.9037 0 0)',
      'sidebar-ring': 'oklch(0.8348 0.1302 160.9080)',
    },
    dark: {
      background: 'oklch(0.1822 0 0)',
      foreground: 'oklch(0.9288 0.0126 255.5078)',
      card: 'oklch(0.2046 0 0)',
      'card-foreground': 'oklch(0.9288 0.0126 255.5078)',
      popover: 'oklch(0.2603 0 0)',
      'popover-foreground': 'oklch(0.7348 0 0)',
      primary: 'oklch(0.4365 0.1044 156.7556)',
      'primary-foreground': 'oklch(0.9213 0.0135 167.1556)',
      secondary: 'oklch(0.2603 0 0)',
      'secondary-foreground': 'oklch(0.9851 0 0)',
      muted: 'oklch(0.2393 0 0)',
      'muted-foreground': 'oklch(0.7122 0 0)',
      accent: 'oklch(0.3132 0 0)',
      'accent-foreground': 'oklch(0.9851 0 0)',
      destructive: 'oklch(0.7040 0.1910 29.7877)',
      'destructive-foreground': 'oklch(0.9368 0.0045 34.3092)',
      border: 'oklch(0.2809 0 0)',
      input: 'oklch(0.2603 0 0)',
      ring: 'oklch(0.8003 0.1821 151.7110)',
      'chart-1': 'oklch(0.8003 0.1821 151.7110)',
      'chart-2': 'oklch(0.7137 0.1434 254.6240)',
      'chart-3': 'oklch(0.7090 0.1592 293.5412)',
      'chart-4': 'oklch(0.8369 0.1644 84.4286)',
      'chart-5': 'oklch(0.7845 0.1325 181.9120)',
      sidebar: 'oklch(0.1822 0 0)',
      'sidebar-foreground': 'oklch(0.6301 0 0)',
      'sidebar-primary': 'oklch(0.4365 0.1044 156.7556)',
      'sidebar-primary-foreground': 'oklch(0.9213 0.0135 167.1556)',
      'sidebar-accent': 'oklch(0.3132 0 0)',
      'sidebar-accent-foreground': 'oklch(0.9851 0 0)',
      'sidebar-border': 'oklch(0.2809 0 0)',
      'sidebar-ring': 'oklch(0.8003 0.1821 151.7110)',
    },
  },
});

const northernLights: ThemeDefinition = parseTweakcnTheme({
  name: 'northern-lights',
  title: 'Northern Lights',
  cssVars: {
    light: {
      background: 'oklch(0.9824 0.0013 286.3757)',
      foreground: 'oklch(0.3211 0 0)',
      card: 'oklch(1.0000 0 0)',
      'card-foreground': 'oklch(0.3211 0 0)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.3211 0 0)',
      primary: 'oklch(0.6487 0.1538 150.3071)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.6746 0.1414 261.3380)',
      'secondary-foreground': 'oklch(1.0000 0 0)',
      muted: 'oklch(0.8828 0.0285 98.1033)',
      'muted-foreground': 'oklch(0.5382 0 0)',
      accent: 'oklch(0.8269 0.1080 211.9627)',
      'accent-foreground': 'oklch(0.3211 0 0)',
      destructive: 'oklch(0.6368 0.2078 25.3313)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8699 0 0)',
      input: 'oklch(0.8699 0 0)',
      ring: 'oklch(0.6487 0.1538 150.3071)',
      'chart-1': 'oklch(0.6487 0.1538 150.3071)',
      'chart-2': 'oklch(0.6746 0.1414 261.3380)',
      'chart-3': 'oklch(0.8269 0.1080 211.9627)',
      'chart-4': 'oklch(0.5880 0.0993 245.7394)',
      'chart-5': 'oklch(0.5905 0.1608 148.2409)',
      sidebar: 'oklch(0.9824 0.0013 286.3757)',
      'sidebar-foreground': 'oklch(0.3211 0 0)',
      'sidebar-primary': 'oklch(0.6487 0.1538 150.3071)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.8269 0.1080 211.9627)',
      'sidebar-accent-foreground': 'oklch(0.3211 0 0)',
      'sidebar-border': 'oklch(0.8699 0 0)',
      'sidebar-ring': 'oklch(0.6487 0.1538 150.3071)',
    },
    dark: {
      background: 'oklch(0.2303 0.0125 264.2926)',
      foreground: 'oklch(0.9219 0 0)',
      card: 'oklch(0.3210 0.0078 223.6661)',
      'card-foreground': 'oklch(0.9219 0 0)',
      popover: 'oklch(0.3210 0.0078 223.6661)',
      'popover-foreground': 'oklch(0.9219 0 0)',
      primary: 'oklch(0.6487 0.1538 150.3071)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.5880 0.0993 245.7394)',
      'secondary-foreground': 'oklch(0.9219 0 0)',
      muted: 'oklch(0.3867 0 0)',
      'muted-foreground': 'oklch(0.7155 0 0)',
      accent: 'oklch(0.6746 0.1414 261.3380)',
      'accent-foreground': 'oklch(0.9219 0 0)',
      destructive: 'oklch(0.6368 0.2078 25.3313)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3867 0 0)',
      input: 'oklch(0.3867 0 0)',
      ring: 'oklch(0.6487 0.1538 150.3071)',
      'chart-1': 'oklch(0.6487 0.1538 150.3071)',
      'chart-2': 'oklch(0.5880 0.0993 245.7394)',
      'chart-3': 'oklch(0.6746 0.1414 261.3380)',
      'chart-4': 'oklch(0.8269 0.1080 211.9627)',
      'chart-5': 'oklch(0.5905 0.1608 148.2409)',
      sidebar: 'oklch(0.2303 0.0125 264.2926)',
      'sidebar-foreground': 'oklch(0.9219 0 0)',
      'sidebar-primary': 'oklch(0.6487 0.1538 150.3071)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.6746 0.1414 261.3380)',
      'sidebar-accent-foreground': 'oklch(0.9219 0 0)',
      'sidebar-border': 'oklch(0.3867 0 0)',
      'sidebar-ring': 'oklch(0.6487 0.1538 150.3071)',
    },
  },
});

const midnightBloom: ThemeDefinition = parseTweakcnTheme({
  name: 'midnight-bloom',
  title: 'Midnight Bloom',
  cssVars: {
    light: {
      background: 'oklch(0.9821 0 0)',
      foreground: 'oklch(0.3211 0 0)',
      card: 'oklch(1.0000 0 0)',
      'card-foreground': 'oklch(0.3211 0 0)',
      popover: 'oklch(1.0000 0 0)',
      'popover-foreground': 'oklch(0.3211 0 0)',
      primary: 'oklch(0.5676 0.2021 283.0838)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.8214 0.0720 249.3482)',
      'secondary-foreground': 'oklch(0.3211 0 0)',
      muted: 'oklch(0.8202 0.0213 91.6163)',
      'muted-foreground': 'oklch(0.5382 0 0)',
      accent: 'oklch(0.6475 0.0642 117.4260)',
      'accent-foreground': 'oklch(1.0000 0 0)',
      destructive: 'oklch(0.6368 0.2078 25.3313)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8699 0 0)',
      input: 'oklch(0.8699 0 0)',
      ring: 'oklch(0.5676 0.2021 283.0838)',
      'chart-1': 'oklch(0.5676 0.2021 283.0838)',
      'chart-2': 'oklch(0.5261 0.1705 314.6534)',
      'chart-3': 'oklch(0.3390 0.1793 301.6848)',
      'chart-4': 'oklch(0.6746 0.1414 261.3380)',
      'chart-5': 'oklch(0.5880 0.0993 245.7394)',
      sidebar: 'oklch(0.9821 0 0)',
      'sidebar-foreground': 'oklch(0.3211 0 0)',
      'sidebar-primary': 'oklch(0.5676 0.2021 283.0838)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.6475 0.0642 117.4260)',
      'sidebar-accent-foreground': 'oklch(1.0000 0 0)',
      'sidebar-border': 'oklch(0.8699 0 0)',
      'sidebar-ring': 'oklch(0.5676 0.2021 283.0838)',
    },
    dark: {
      background: 'oklch(0.2303 0.0125 264.2926)',
      foreground: 'oklch(0.9219 0 0)',
      card: 'oklch(0.3210 0.0078 223.6661)',
      'card-foreground': 'oklch(0.9219 0 0)',
      popover: 'oklch(0.3210 0.0078 223.6661)',
      'popover-foreground': 'oklch(0.9219 0 0)',
      primary: 'oklch(0.5676 0.2021 283.0838)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.3390 0.1793 301.6848)',
      'secondary-foreground': 'oklch(0.9219 0 0)',
      muted: 'oklch(0.3867 0 0)',
      'muted-foreground': 'oklch(0.7155 0 0)',
      accent: 'oklch(0.6746 0.1414 261.3380)',
      'accent-foreground': 'oklch(0.9219 0 0)',
      destructive: 'oklch(0.6368 0.2078 25.3313)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3867 0 0)',
      input: 'oklch(0.3867 0 0)',
      ring: 'oklch(0.5676 0.2021 283.0838)',
      'chart-1': 'oklch(0.5676 0.2021 283.0838)',
      'chart-2': 'oklch(0.5261 0.1705 314.6534)',
      'chart-3': 'oklch(0.3390 0.1793 301.6848)',
      'chart-4': 'oklch(0.6746 0.1414 261.3380)',
      'chart-5': 'oklch(0.5880 0.0993 245.7394)',
      sidebar: 'oklch(0.2303 0.0125 264.2926)',
      'sidebar-foreground': 'oklch(0.9219 0 0)',
      'sidebar-primary': 'oklch(0.5676 0.2021 283.0838)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.6746 0.1414 261.3380)',
      'sidebar-accent-foreground': 'oklch(0.9219 0 0)',
      'sidebar-border': 'oklch(0.3867 0 0)',
      'sidebar-ring': 'oklch(0.5676 0.2021 283.0838)',
    },
  },
});

const solarDusk: ThemeDefinition = parseTweakcnTheme({
  name: 'solar-dusk',
  title: 'Solar Dusk',
  cssVars: {
    light: {
      background: 'oklch(0.9885 0.0057 84.5659)',
      foreground: 'oklch(0.3660 0.0251 49.6085)',
      card: 'oklch(0.9686 0.0091 78.2818)',
      'card-foreground': 'oklch(0.3660 0.0251 49.6085)',
      popover: 'oklch(0.9686 0.0091 78.2818)',
      'popover-foreground': 'oklch(0.3660 0.0251 49.6085)',
      primary: 'oklch(0.5553 0.1455 48.9975)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.8276 0.0752 74.4400)',
      'secondary-foreground': 'oklch(0.4444 0.0096 73.6390)',
      muted: 'oklch(0.9363 0.0218 83.2637)',
      'muted-foreground': 'oklch(0.5534 0.0116 58.0708)',
      accent: 'oklch(0.9000 0.0500 74.9889)',
      'accent-foreground': 'oklch(0.4444 0.0096 73.6390)',
      destructive: 'oklch(0.4437 0.1613 26.8994)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.8866 0.0404 89.6994)',
      input: 'oklch(0.8866 0.0404 89.6994)',
      ring: 'oklch(0.5553 0.1455 48.9975)',
      'chart-1': 'oklch(0.5553 0.1455 48.9975)',
      'chart-2': 'oklch(0.5534 0.0116 58.0708)',
      'chart-3': 'oklch(0.5538 0.1207 66.4416)',
      'chart-4': 'oklch(0.5534 0.0116 58.0708)',
      'chart-5': 'oklch(0.6806 0.1423 75.8340)',
      sidebar: 'oklch(0.9363 0.0218 83.2637)',
      'sidebar-foreground': 'oklch(0.3660 0.0251 49.6085)',
      'sidebar-primary': 'oklch(0.5553 0.1455 48.9975)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.5538 0.1207 66.4416)',
      'sidebar-accent-foreground': 'oklch(1.0000 0 0)',
      'sidebar-border': 'oklch(0.8866 0.0404 89.6994)',
      'sidebar-ring': 'oklch(0.5553 0.1455 48.9975)',
    },
    dark: {
      background: 'oklch(0.2161 0.0061 56.0434)',
      foreground: 'oklch(0.9699 0.0013 106.4238)',
      card: 'oklch(0.2685 0.0063 34.2976)',
      'card-foreground': 'oklch(0.9699 0.0013 106.4238)',
      popover: 'oklch(0.2685 0.0063 34.2976)',
      'popover-foreground': 'oklch(0.9699 0.0013 106.4238)',
      primary: 'oklch(0.7049 0.1867 47.6044)',
      'primary-foreground': 'oklch(1.0000 0 0)',
      secondary: 'oklch(0.4444 0.0096 73.6390)',
      'secondary-foreground': 'oklch(0.9232 0.0026 48.7171)',
      muted: 'oklch(0.2330 0.0073 67.4563)',
      'muted-foreground': 'oklch(0.7161 0.0091 56.2590)',
      accent: 'oklch(0.3598 0.0497 229.3202)',
      'accent-foreground': 'oklch(0.9232 0.0026 48.7171)',
      destructive: 'oklch(0.5771 0.2152 27.3250)',
      'destructive-foreground': 'oklch(1.0000 0 0)',
      border: 'oklch(0.3741 0.0087 67.5582)',
      input: 'oklch(0.3741 0.0087 67.5582)',
      ring: 'oklch(0.7049 0.1867 47.6044)',
      'chart-1': 'oklch(0.7049 0.1867 47.6044)',
      'chart-2': 'oklch(0.6847 0.1479 237.3225)',
      'chart-3': 'oklch(0.7952 0.1617 86.0468)',
      'chart-4': 'oklch(0.7161 0.0091 56.2590)',
      'chart-5': 'oklch(0.5534 0.0116 58.0708)',
      sidebar: 'oklch(0.2685 0.0063 34.2976)',
      'sidebar-foreground': 'oklch(0.9699 0.0013 106.4238)',
      'sidebar-primary': 'oklch(0.7049 0.1867 47.6044)',
      'sidebar-primary-foreground': 'oklch(1.0000 0 0)',
      'sidebar-accent': 'oklch(0.6847 0.1479 237.3225)',
      'sidebar-accent-foreground': 'oklch(0.2839 0.0734 254.5378)',
      'sidebar-border': 'oklch(0.3741 0.0087 67.5582)',
      'sidebar-ring': 'oklch(0.7049 0.1867 47.6044)',
    },
  },
});

export const THEME_REGISTRY: ThemeDefinition[] = [
  neutral,
  t3Chat,
  catppuccin,
  claude,
  vercel,
  mono,
  cyberpunk,
  doom64,
  supabase,
  northernLights,
  midnightBloom,
  solarDusk,
];

export const DEFAULT_THEME_ID = 'neutral';

export function getThemeById(id: string): ThemeDefinition | undefined {
  return THEME_REGISTRY.find((t) => t.id === id);
}
